<template>
  <AIVDialog
    v-model="isVisible"
    :title="isClockedInEditMode ? 'Edit Shift Details' : 'Edit Shift'"
    width="800px"
    height="85vh"
    class="mobile-friendly-modal edit-notes-modal"
  >
    <div class="edit-notes-content">
      <div v-if="validationErrors.length > 0" class="validation-error-summary">
        <div class="validation-error-header">
          <i class="el-icon-warning"></i> Please fix the following {{ validationErrors.length }} error(s):
        </div>
        <ul class="validation-error-list">
          <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
        </ul>
      </div>
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p><strong>Date:</strong> {{ formatTableDate(shift?.date) }}</p>
        <p>
          <strong>Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduled_start || shift?.startTime,
              shift?.scheduled_end || shift?.endTime,
              shift?.date,
            )
          }}
        </p>
        <p><strong>Client:</strong> {{ formatClient(shift) }}</p>
        <p><strong>Caregiver:</strong> {{ formatCaregiver(shift) }}</p>
      </div>

      <!-- Read-only Client Tasks (Group Shifts) -->
      <div v-if="isGroupShift && clientTaskData.length > 0" class="mb-4">
        <h5>Client Tasks</h5>
        <div class="client-tasks-list">
          <div
            v-for="task in clientTaskData"
            :key="task.id"
            class="client-task-card"
          >
            <div class="client-task-header">{{ task.clientName }}</div>
            <div class="client-task-rows">
              <!-- Pick Up -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-car-side"></i>
                  <span class="client-task-label">Pick Up</span>
                </div>
                <div class="client-task-value">
                  <span v-if="task.pickupTime" class="client-task-completed">
                    {{ formatClockTime(task.pickupTime) }}
                    <span v-if="task.pickupCaregiverName" class="client-task-by"> - {{ task.pickupCaregiverName }}</span>
                  </span>
                  <span v-else class="client-task-pending">Not completed</span>
                </div>
              </div>
              <!-- Drop Off -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Drop Off</span>
                </div>
                <div class="client-task-value">
                  <span v-if="task.dropoffTime" class="client-task-completed">
                    {{ formatClockTime(task.dropoffTime) }}
                    <span v-if="task.dropoffCaregiverName" class="client-task-by"> - {{ task.dropoffCaregiverName }}</span>
                  </span>
                  <span v-else class="client-task-pending">Not completed</span>
                </div>
              </div>
              <!-- Shift Notes Status -->
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-clipboard"></i>
                  <span class="client-task-label">Shift Notes</span>
                </div>
                <div class="client-task-value">
                  <span v-if="task.shiftNotesCompleted" class="client-task-completed">
                    Completed
                    <span v-if="task.shiftNotesCaregiverName" class="client-task-by"> - {{ task.shiftNotesCaregiverName }}</span>
                  </span>
                  <span v-else class="client-task-pending">Not completed</span>
                </div>
              </div>
              <!-- Medications -->
              <div v-if="task.medicationsCaregiverId" class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-pills"></i>
                  <span class="client-task-label">Medications</span>
                </div>
                <div class="client-task-value">
                  <span v-if="task.medicationsCompleted" class="client-task-completed">
                    Completed
                    <span v-if="task.medicationsCaregiverName" class="client-task-by"> - {{ task.medicationsCaregiverName }}</span>
                  </span>
                  <span v-else class="client-task-pending">Not completed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Shift Notes (only for clients assigned to current user) -->
      <div v-if="isGroupShift && groupNotesFetched && myClientNotes.length > 0" class="mb-4">
        <h5>Shift Notes</h5>
        <div v-if="loadingGroupNotes" class="text-center">
          <i class="fa-solid fa-spinner fa-spin"></i> Loading...
        </div>
        <div v-else>
          <div
            v-for="task in myClientNotes"
            :key="'notes-' + task.clientId"
            class="client-notes-card mb-3"
          >
            <div
              class="client-notes-header"
              @click="toggleClientNotes(task.clientId)"
            >
              <span>{{ task.clientName }}</span>
              <i
                :class="expandedClients[task.clientId] ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
              ></i>
            </div>
            <div v-if="expandedClients[task.clientId]" class="client-notes-body">
              <!-- Service Code Questions -->
              <div
                v-for="q in serviceCodeQuestions"
                :key="'q-' + q.id + '-' + task.clientId"
                class="mb-3"
              >
                <label class="form-label">
                  {{ q.question_text }}
                  <span v-if="q.required" class="required-indicator">*</span>
                </label>
                <div
                  v-if="q.description"
                  class="shift-notes-description mb-2"
                  style="white-space: pre-line;"
                >{{ q.description }}</div>
                <el-input
                  v-if="q.question_type === 'free_response' || !q.question_type"
                  :value="getAnswer(task.clientId, 'questionAnswers', q.id)"
                  @input="setAnswer(task.clientId, 'questionAnswers', q.id, $event)"
                  type="textarea"
                  :rows="2"
                  placeholder="Enter your answer..."
                />
                <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
                  <el-button
                    :type="getAnswer(task.clientId, 'questionAnswers', q.id) === 'yes' ? 'primary' : 'default'"
                    size="medium"
                    @click="setAnswer(task.clientId, 'questionAnswers', q.id, 'yes')"
                  >Yes</el-button>
                  <el-button
                    :type="getAnswer(task.clientId, 'questionAnswers', q.id) === 'no' ? 'danger' : 'default'"
                    size="medium"
                    @click="setAnswer(task.clientId, 'questionAnswers', q.id, 'no')"
                  >No</el-button>
                </div>
                <AIVSelect
                  v-else-if="q.question_type === 'select'"
                  :value="getAnswer(task.clientId, 'questionAnswers', q.id)"
                  @input="setAnswer(task.clientId, 'questionAnswers', q.id, $event)"
                  :options="(q.options || []).map((o, i) => ({ id: o, name: o }))"
                  placeholder="Select an answer"
                  :custom-style="{ width: '100%' }"
                />
                <AIVSelect
                  v-else-if="q.question_type === 'multi_select'"
                  :value="parseMultiAnswer(getAnswer(task.clientId, 'questionAnswers', q.id))"
                  @input="setAnswer(task.clientId, 'questionAnswers', q.id, joinMultiAnswer($event))"
                  :options="(q.options || []).map((o) => ({ id: o, name: o }))"
                  placeholder="Select all that apply"
                  :multi-select="true"
                  :searchable="true"
                  :custom-style="{ width: '100%' }"
                />
              </div>

              <!-- Client Goals -->
              <div
                v-for="goal in (clientGoals[task.clientId] || [])"
                :key="'g-' + goal.id + '-' + task.clientId"
                class="mb-3"
              >
                <label class="form-label">{{ goal.question_text }}</label>
                <el-input
                  v-if="goal.question_type === 'free_response' || !goal.question_type"
                  :value="getAnswer(task.clientId, 'goalAnswers', goal.id)"
                  @input="setAnswer(task.clientId, 'goalAnswers', goal.id, $event)"
                  type="textarea"
                  :rows="2"
                  placeholder="Enter your answer..."
                />
                <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
                  <el-button
                    :type="getAnswer(task.clientId, 'goalAnswers', goal.id) === 'yes' ? 'primary' : 'default'"
                    size="medium"
                    @click="setAnswer(task.clientId, 'goalAnswers', goal.id, 'yes')"
                  >Yes</el-button>
                  <el-button
                    :type="getAnswer(task.clientId, 'goalAnswers', goal.id) === 'no' ? 'danger' : 'default'"
                    size="medium"
                    @click="setAnswer(task.clientId, 'goalAnswers', goal.id, 'no')"
                  >No</el-button>
                </div>
              </div>

              <!-- General Shift Notes (concerns) -->
              <div class="mb-3">
                <label class="form-label">Do you have any concerns about this individual today? <span class="required-indicator">*</span></label>
                <el-input
                  :value="notesData[task.clientId] ? notesData[task.clientId].shiftNotes : ''"
                  @input="setShiftNotes(task.clientId, $event)"
                  type="textarea"
                  :rows="3"
                  placeholder="Enter your answer..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Non-Group Shift Fields (Payer, Service Code, Activities, Notes) -->
      <template v-if="!isGroupShift">
      <!-- Approved shifts: documentation-only edit -->
      <div v-if="isApprovedShift" class="service-code-tooltip mb-3">
        This shift is approved, so payer, client, and service code are locked.
        Use Change Service Code or Undo Approval to modify them.
      </div>

      <!-- Payer Type Selection -->
      <div class="mb-3">
        <label class="form-label">Payer Type</label>
        <AIVSelect
          v-model="selectedPayer"
          :options="payerOptions"
          placeholder="Select payer type"
          :searchable="false"
          :disabled="isApprovedShift"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Client Selection (VR only - DSPD clients are shown in shift details) -->
      <div v-if="selectedPayer === 'VR'" class="mb-3">
        <label class="form-label">Client</label>
        <AIVSelect
          v-model="selectedClient"
          :options="clients"
          placeholder="Select client"
          :searchable="true"
          :disabled="isApprovedShift"
          :custom-style="{ width: '100%' }"
          :loading="loadingClients"
        />
      </div>

      <!-- Service Code Selection -->
      <div class="mb-3">
        <label class="form-label">Service Code</label>
        <div
          v-if="shouldShowServiceCodeTooltip"
          class="service-code-tooltip mb-2"
        >
          {{ getServiceCodeTooltipContent }}
        </div>
        <AIVSelect
          v-model="selectedServiceCodes"
          :options="availableServiceCodes"
          :placeholder="getServiceCodePlaceholder"
          :multi-select="false"
          :searchable="true"
          :disabled="isServiceCodeDisabled || isApprovedShift"
          :custom-style="{ width: '100%' }"
        />
      </div>

      <!-- Generic VR activity pickers (LS/SED/SB and any other VR code with an
           admin-managed activity list) -->
      <template v-if="!isClockedInEditMode">
        <div
          v-for="code in vrCodesWithActivities"
          :key="'vract-' + code"
          class="mb-3"
        >
          <label class="form-label">
            {{ vrActivityState[code].name }} — Activity Completed
            <template v-if="maxForCode(code) != null">(Select up to {{ maxForCode(code) }})</template>
          </label>
          <AIVSelect
            :value="vrActivityState[code].selectedIds"
            @input="onVrActivitySelect(code, $event)"
            :options="vrActivityState[code].options"
            placeholder="Select activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
          />
        </div>
      </template>

      <!-- Legacy Job Development fields (CE/SE/SJBT/TWE): shown only for
           shifts already documented on the jd_* columns, or when the code
           has no configured questions (see showJdFields) -->
      <div v-if="!isClockedInEditMode && showJdFields">
        <div class="mb-3">
          <label class="form-label">Job Development Activity Completed</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>Places applied to that shift</li>
                <li>Employer follow up completed</li>
                <li>Informational interviews completed</li>
                <li>Resume development progress</li>
                <li>
                  Information about where an interview occurred and preparation
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdActivity"
            type="textarea"
            :rows="4"
            placeholder="Describe job development activities completed..."
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Outcome of Activity</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>
                  List if you helped the client apply or if they will be
                  applying on their own
                </li>
                <li>Describe how follow up went</li>
                <li>
                  Describe how the interview went and the date they will hear
                  back
                </li>
                <li>
                  Describe if you were able to finish the resume development or
                  if information is still needed
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdOutcome"
            type="textarea"
            :rows="4"
            placeholder="Describe the outcome of activities..."
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Next Steps Forward</label>
          <div class="shift-notes-description mb-2">
            <div>
              Please include:
              <ul>
                <li>
                  What is the plan for your next meeting for job development and
                  are you planning on doing any job development on their behalf
                  before the next meeting?
                </li>
                <li>
                  If there is any follow up with employers or applications that
                  are supposed to be completed by the client, include that
                  information here
                </li>
              </ul>
            </div>
          </div>
          <el-input
            v-model="jdNextSteps"
            type="textarea"
            :rows="4"
            placeholder="Describe next steps forward..."
          />
        </div>
      </div>

      <!-- JC Service Code Specific Fields -->
      <div v-if="!isClockedInEditMode && isJCService">
        <div class="mb-3">
          <label class="form-label"
            >Primary Job Coaching Activities (Select up to 3)</label
          >
          <AIVSelect
            v-model="selectedJCPrimaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select primary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>

        <div class="mb-3">
          <label class="form-label"
            >Secondary Job Coaching Activities (Select up to 3)</label
          >
          <AIVSelect
            v-model="selectedJCSecondaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select secondary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>
      </div>

      <!-- DSPD 1-on-1 shift: service code questions, goals, and concerns (hidden when clocked-in) -->
      <div v-if="!isClockedInEditMode && isDSPDOneOnOneShift && dspdOneOnOneLoaded">
        <h5 class="mb-3">Shift Notes</h5>
        <div
          v-for="q in visibleDspdQuestions"
          :key="'dspd-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" class="required-indicator">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            :value="dspdQuestionAnswers[q.id] || ''"
            @input="$set(dspdQuestionAnswers, q.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="dspdQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(dspdQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="dspdQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(dspdQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <AIVSelect
            v-else-if="q.question_type === 'select'"
            :value="dspdQuestionAnswers[q.id] || ''"
            @input="$set(dspdQuestionAnswers, q.id, $event)"
            :options="(q.options || []).map((o) => ({ id: o, name: o }))"
            placeholder="Select an answer"
            :custom-style="{ width: '100%' }"
          />
          <AIVSelect
            v-else-if="q.question_type === 'multi_select'"
            :value="parseMultiAnswer(dspdQuestionAnswers[q.id])"
            @input="$set(dspdQuestionAnswers, q.id, joinMultiAnswer($event))"
            :options="(q.options || []).map((o) => ({ id: o, name: o }))"
            placeholder="Select all that apply"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
          />
        </div>

        <div
          v-for="goal in dspdClientGoals"
          :key="'dspd-g-' + goal.id"
          class="mb-3"
        >
          <label class="form-label">{{ goal.question_text }}</label>
          <el-input
            v-if="goal.question_type === 'free_response' || !goal.question_type"
            :value="dspdGoalAnswers[goal.id] || ''"
            @input="$set(dspdGoalAnswers, goal.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="dspdGoalAnswers[goal.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(dspdGoalAnswers, goal.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="dspdGoalAnswers[goal.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(dspdGoalAnswers, goal.id, 'no')"
            >No</el-button>
          </div>
        </div>

        <div v-if="showConcernsField" class="mb-3">
          <label class="form-label">
            Do you have any concerns about this individual today?
            <span class="required-indicator">*</span>
          </label>
          <el-input
            v-model="dspdConcerns"
            type="textarea"
            :rows="3"
            placeholder="Enter your answer..."
          />
        </div>
      </div>

      <!-- VR 1-on-1 admin-defined service-code questions (backfillable post clock-out) -->
      <div v-if="!isClockedInEditMode && isVRShift && vrServiceCodeQuestions.length && !showJdFields">
        <h5 class="mb-3">Shift Questions</h5>
        <div
          v-for="q in vrServiceCodeQuestions"
          :key="'vr-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" class="required-indicator">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            :value="vrQuestionAnswers[q.id] || ''"
            @input="$set(vrQuestionAnswers, q.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="vrQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(vrQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="vrQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(vrQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <AIVSelect
            v-else-if="q.question_type === 'select'"
            :value="vrQuestionAnswers[q.id] || ''"
            @input="$set(vrQuestionAnswers, q.id, $event)"
            :options="(q.options || []).map((o) => ({ id: o, name: o }))"
            placeholder="Select an answer"
            :custom-style="{ width: '100%' }"
          />
          <AIVSelect
            v-else-if="q.question_type === 'multi_select'"
            :value="parseMultiAnswer(vrQuestionAnswers[q.id])"
            @input="$set(vrQuestionAnswers, q.id, joinMultiAnswer($event))"
            :options="(q.options || []).map((o) => ({ id: o, name: o }))"
            placeholder="Select all that apply"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
          />
        </div>
      </div>

      <!-- Medication Administration (DSPD 1-on-1 Shifts) -->
      <div v-if="hasMedicationsToShow" class="mb-3">
        <h5>Medication Administration</h5>
        <div v-if="loadingMedications" class="text-center">
          <i class="fa-solid fa-spinner fa-spin"></i> Loading medications...
        </div>
        <div v-else>
          <div
            v-for="med in clientMedications"
            :key="'edit-med-' + med.id"
            class="medication-admin-item mb-2"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ med.name }}</strong> - {{ med.dosage }}
                <span v-if="med.frequency">({{ med.frequency }})</span>
              </div>
              <el-switch
                v-if="medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`]"
                v-model="medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`].wasAdministered"
                active-text="Given"
                inactive-text="Skipped"
              />
            </div>
            <div
              v-if="medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`] && !medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`].wasAdministered"
              class="mt-1"
            >
              <el-input
                v-model="medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`].skipReason"
                placeholder="Reason for skipping (required)"
                size="small"
              />
            </div>
            <div
              v-if="medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`] && medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`].wasAdministered"
              class="mt-1"
            >
              <span style="color: #e0e0e0; font-size: 0.85rem;">Time Given:</span>
              <AIVTimeSelect
                v-model="medicationAdministrations[`${(shift?.clients || shift?.originalData?.clients || [])[0]?.id}_${med.id}`].administeredAtTime"
                :minute-interval="5"
                :allow-exact-minutes="true"
                placeholder="Time given"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Non-DSPD shift notes (hidden when clocked-in edit mode).
           Hidden when admin-configured shift-note questions apply — those
           questions replace the freeform notes box. -->
      <div class="mb-3" v-if="!isClockedInEditMode && !isJDService && !isDSPDOneOnOneShift && !hasShiftNoteQuestions">
        <label class="form-label">Shift Notes</label>
        <div
          v-if="isLSService || isSEDService"
          class="shift-notes-description mb-2"
        >
          <div>
            Please include:
            <ul>
              <li>How your client responded to the lesson</li>
              <li>Issues experienced during the lesson</li>
              <li>Plan for next life skills meeting</li>
              <li>Successful outcomes from the lessons</li>
            </ul>
          </div>
        </div>
        <div v-if="isSBService" class="shift-notes-description mb-2">
          <div>
            Please include:
            <ul>
              <li>
                Update on progress of forms being submitted or response from
                follow up
              </li>
              <li>Any documentation that is still needing to be submitted</li>
            </ul>
          </div>
        </div>
        <div v-if="isJCService" class="shift-notes-description mb-2">
          <div>
            Please include:
            <ul>
              <li>
                Challenges faced or issues holding the client back from
                independence (Most Important)
              </li>
              <li>
                What are we doing to overcome those challenges? Each challenge
                should have a way or idea to implement to overcome it
              </li>
              <li>
                A run down of how the shift went (like a play by play but
                doesn't need to be as specific)
              </li>
              <li>Important work updates</li>
              <li>
                Client successes and where they are doing well and don't need
                support (This will help determine where support can be faded)
              </li>
            </ul>
          </div>
        </div>
        <el-input
          v-model="shiftNotes"
          type="textarea"
          :rows="vrCodesWithActivities.length || isJCService ? 6 : 3"
          placeholder="Shift Notes"
        />
      </div>
      </template>
      <!-- End Non-Group Shift Fields -->

      <!-- Admin edits require a reason (server-enforced on PUT /api/shifts/:id).
           Group shifts save via completeShiftNotes, which doesn't require one. -->
      <div v-if="isAdminUser && !isGroupShift" class="mb-3">
        <label class="form-label">Reason for Edit</label>
        <el-input
          v-model="adminChangeReason"
          type="textarea"
          :rows="2"
          placeholder="Reason for Edit"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">
          <i class="fa-solid fa-save"></i> Save Changes
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { jobCoachingActivitiesService } from "src/serviceHandlers/jobCoachingActivitiesService";
import vrActivitiesMixin from "src/mixins/vrActivitiesMixin";
import { serviceCodesService } from "src/serviceHandlers/serviceCodesService";
import { nonServiceCodesService } from "src/serviceHandlers/nonServiceCodesService";
import { clientsService } from "src/serviceHandlers/clientsService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { isDSPDGroupShift } from "@/utils/shiftStatus";
import { enqueue as offlineEnqueue, getPending as getOfflinePending } from "@/utils/offlineQueue";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "EditShiftNotesModal",
  components: { AIVDialog, AIVSelect, AIVTimeSelect },
  mixins: [vrActivitiesMixin],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      saving: false,
      validationErrors: [],
      adminChangeReason: "",
      isLoadingExistingData: false, // Flag to prevent watcher from clearing data during initial load
      isChangingPayer: false, // Flag to prevent clearing notes when payer change triggers service code clear
      // Payer and Service Codes
      selectedPayer: null,
      selectedClient: null,
      selectedServiceCodes: null,
      payerOptions: [
        { id: "VR", name: "VR" },
        { id: "DSPD", name: "DSPD" },
        { id: "Non Service", name: "Non Service" },
      ],
      clients: [],
      serviceCodes: [],
      dspdServiceCodes: [],
      nonServiceCodes: [],
      availableServiceCodes: [],
      loadingServiceCodes: false,
      loadingClients: false,
      // Activity selections
      selectedActivities: [],
      selectedSEDActivities: [],
      selectedSBActivities: [],
      selectedJCPrimaryActivities: [],
      selectedJCSecondaryActivities: [],
      // Notes
      shiftNotes: "",
      jdActivity: "",
      jdOutcome: "",
      jdNextSteps: "",
      // Group shift question/goal data
      serviceCodeQuestions: [],
      clientGoals: {},
      notesData: {},
      expandedClients: {},
      loadingGroupNotes: false,
      groupNotesFetched: false,
      // DSPD 1-on-1 shift note data
      dspdServiceCodeQuestions: [],
      dspdClientGoals: [],
      dspdQuestionAnswers: {},
      dspdGoalAnswers: {},
      dspdConcerns: "",
      dspdOneOnOneLoaded: false,
      // VR 1-on-1 service-code question data (admin-defined, like ClockOutModal)
      vrServiceCodeQuestions: [],
      vrQuestionAnswers: {},
      vrNotesLoaded: false,
      // Medication data for 1-on-1 DSPD shifts
      clientMedications: [],
      medicationAdministrations: {},
      loadingMedications: false,
      // Activity options
      lsActivities: [],
      sedActivities: [],
      sbActivities: [],
      jobCoachingActivities: [],
      loadingJobCoachingActivities: false,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit("update:visible", false);
        }
      },
    },

    userId() {
      const loginStore = useLoginStore();
      return loginStore.user?.id;
    },

    isAdminUser() {
      const loginStore = useLoginStore();
      return loginStore.user?.user_type === "Admin";
    },

    isGroupShift() {
      if (!this.shift) return false;
      return isDSPDGroupShift(this.shift) || isDSPDGroupShift(this.shift.originalData);
    },

    isDSPDOneOnOneShift() {
      if (!this.shift) return false;
      let payer = this.selectedPayer || this.shift.payer || this.shift.originalData?.payer;
      if (!payer) {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "DSPD" && !this.isGroupShift;
    },

    isVRShift() {
      if (!this.shift) return false;
      let payer = this.selectedPayer || this.shift.payer || this.shift.originalData?.payer;
      if (!payer) {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "VR" && !this.isGroupShift;
    },

    // True when this shift has admin-configured shift-note questions
    // (shift_note_questions table). When present, those questions replace
    // the freeform "Shift Notes" box.
    hasShiftNoteQuestions() {
      return (
        this.vrServiceCodeQuestions.length > 0 ||
        this.dspdServiceCodeQuestions.length > 0
      );
    },

    // True when shift is clocked-in (not clocked-out) and is a 1-on-1 DSPD shift.
    // In this mode, only payer type and service code are editable.
    // Missed shifts should NOT be treated as clocked-in even if clock_in exists without clock_out.
    isClockedInEditMode() {
      if (!this.shift) return false;
      const status = (this.shift.shift_status || this.shift.status || this.shift.originalData?.shift_status || this.shift.originalData?.status || "").toLowerCase();
      if (status === "missed") return false;
      const clockIn = this.shift.clock_in || this.shift.originalData?.clock_in;
      const clockOut = this.shift.clock_out || this.shift.originalData?.clock_out;
      const isClockedIn = clockIn && !clockOut;
      return isClockedIn && this.isDSPDOneOnOneShift;
    },

    hasMedicationsToShow() {
      return this.isDSPDOneOnOneShift && !this.isClockedInEditMode && this.clientMedications.length > 0;
    },

    clientTaskData() {
      if (!this.isGroupShift) return [];
      const clientDetails = this.shift.clientDetails || this.shift.originalData?.clientDetails || [];
      const caregivers = this.shift.caregivers || this.shift.originalData?.caregivers || [];
      const getCaregiverName = (caregiver, caregiverId) => {
        if (caregiver) {
          return `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
        }
        if (caregiverId) {
          const cg = caregivers.find(c => Number(c.id) === Number(caregiverId));
          if (cg) return `${cg.first_name || ""} ${cg.last_name || ""}`.trim();
        }
        return null;
      };
      return clientDetails.map((cd) => {
        const client = cd.client || (this.shift.clients || []).find(c => c.id === cd.id_clients) || {};
        const clientName = client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unknown Client";
        return {
          id: cd.id,
          clientId: cd.id_clients,
          clientName,
          pickupCaregiverId: cd.pickup_caregiver_id,
          pickupTime: cd.pickup_time,
          pickupCaregiverName: getCaregiverName(cd.pickupCaregiver, cd.pickup_caregiver_id),
          dropoffCaregiverId: cd.dropoff_caregiver_id,
          dropoffTime: cd.dropoff_time,
          dropoffCaregiverName: getCaregiverName(cd.dropoffCaregiver, cd.dropoff_caregiver_id),
          shiftNotesCompleted: cd.shift_notes_completed,
          shiftNotesCaregiverId: cd.shift_notes_caregiver_id,
          shiftNotesCaregiverName: getCaregiverName(cd.shiftNotesCaregiver, cd.shift_notes_caregiver_id),
          medicationsCaregiverId: cd.medications_caregiver_id,
          medicationsCompleted: cd.medications_completed,
          medicationsCaregiverName: getCaregiverName(cd.medicationsCaregiver, cd.medications_caregiver_id),
        };
      });
    },

    // Clients where the logged-in user is the assigned shift notes caregiver
    myClientNotes() {
      return this.clientTaskData.filter(
        (task) => Number(task.shiftNotesCaregiverId) === Number(this.userId),
      );
    },

    // Get the DSPD service code for this shift (DSG or DSP)
    shiftServiceCode() {
      const serviceCodes = this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
      const dspdCode = serviceCodes.find(sc => sc.code === 'DSG' || sc.code === 'DSP');
      return dspdCode ? dspdCode.code : null;
    },

    serviceCode() {
      // Use currently selected service code, not the original shift data
      // This allows activity fields to update when service code is changed
      if (!this.selectedServiceCodes) return null;

      // Handle both single value (Non Service) and array (VR)
      const selectedId = Array.isArray(this.selectedServiceCodes)
        ? this.selectedServiceCodes[0]
        : this.selectedServiceCodes;

      if (!selectedId) return null;

      // Look up the code from available service codes
      const serviceCode = this.availableServiceCodes.find(sc => sc.id === selectedId);
      return serviceCode ? serviceCode.name : null; // name contains the code (e.g., "LS", "JC")
    },

    isLSService() {
      return this.serviceCode === "LS";
    },

    isSEDService() {
      return this.serviceCode === "SED";
    },

    isSBService() {
      return this.serviceCode === "SB";
    },

    isJDService() {
      // Job Development codes: CE, SE, SJBT, TWE
      return ["CE", "SE", "SJBT", "TWE"].includes(this.serviceCode);
    },

    // Legacy JD documentation already stored on the shift's jd_* columns
    // (from before the JD fields became configurable shift_note_questions)
    hasLegacyJdData() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!(
        s.jd_activity || s.jd_outcome || s.jd_next_steps ||
        o.jd_activity || o.jd_outcome || o.jd_next_steps
      );
    },

    // Show the bespoke jd_* fields only for shifts already documented that
    // way, or as a fallback when the JD code has no configured questions.
    showJdFields() {
      return (
        this.isJDService &&
        (this.hasLegacyJdData || this.vrServiceCodeQuestions.length === 0)
      );
    },

    // Legacy DSPD 1-on-1 concerns already stored as shifts.shift_notes
    hasLegacyConcerns() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!((s.shift_notes || o.shift_notes || "").toString().trim());
    },

    // Hardcoded concerns textarea: legacy shifts and question-less codes only
    showConcernsField() {
      return (
        this.isDSPDOneOnOneShift &&
        (this.hasLegacyConcerns || this.dspdServiceCodeQuestions.length === 0)
      );
    },

    // Rendered DSPD question list — hides the seeded concerns question while
    // the legacy concerns textarea is active.
    visibleDspdQuestions() {
      if (!this.showConcernsField) return this.dspdServiceCodeQuestions;
      return this.dspdServiceCodeQuestions.filter(
        (q) => q.question_key !== "dspd_concerns",
      );
    },

    isJCService() {
      return this.serviceCode === "JC";
    },

    // Approved shifts are documentation-only edits: notes, question answers,
    // and activities. Payer/client/service code are locked because their auth
    // accounting already ran at approval (Change Service Code / Undo Approval
    // are the sanctioned paths for those).
    isApprovedShift() {
      const status = (
        this.shift?.status ||
        this.shift?.shift_status ||
        this.shift?.originalData?.shift_status ||
        ""
      ).toLowerCase();
      return status === "approved";
    },

    isServiceCodeDisabled() {
      // For VR payer, service codes should be disabled until a client is selected
      if (this.selectedPayer === "VR") {
        return !this.selectedClient;
      }
      // For DSPD and Non Service payers, service codes are always enabled
      return false;
    },

    shouldShowServiceCodeTooltip() {
      // Show tooltip when disabled OR when no service codes are available
      return (
        this.isServiceCodeDisabled ||
        (this.selectedPayer === "VR" &&
          this.selectedClient &&
          this.availableServiceCodes.length === 0)
      );
    },

    getServiceCodeTooltipContent() {
      // Only show tooltip for VR payer when client not selected
      if (this.selectedPayer === "VR" && !this.selectedClient) {
        return "Please select a client first to see available service codes";
      }
      // Show message if no service codes available after client is selected
      if (
        this.selectedPayer === "VR" &&
        this.selectedClient &&
        this.availableServiceCodes.length === 0
      ) {
        return "No service codes available for this client's active authorizations";
      }
      return "";
    },

    getServiceCodePlaceholder() {
      if (this.selectedPayer === "Non Service") {
        return "Select service code";
      }
      if (
        this.selectedPayer === "VR" &&
        this.selectedClient &&
        this.availableServiceCodes.length === 0
      ) {
        return "No service codes available";
      }
      return "Select service code";
    },
  },

  watch: {
    async visible(newVal) {
      if (newVal) {
        await this.loadServiceCodes();
        await this.loadExistingData(); // Load existing data first to set selectedServiceCodes
        await this.loadActivityOptions(); // Then load activity options based on selected service code
        await this.loadExistingActivityData(); // Finally, map existing activity names to IDs
        // For group shifts, load questions and goals
        if (this.isGroupShift) {
          await this.loadGroupShiftNotes();
        }
        // For DSPD 1-on-1 shifts, load questions and goals (skip when clocked-in, only payer/service code editable)
        if (this.isDSPDOneOnOneShift && !this.isClockedInEditMode) {
          await this.loadDSPDOneOnOneNotes();
          await this.loadMedicationData();
        }
        // For VR 1-on-1 shifts, load admin-defined service-code questions
        if (this.isVRShift && !this.isClockedInEditMode) {
          await this.loadVRNotes();
        }
      } else {
        this.resetForm();
      }
    },

    async selectedPayer(newPayer, oldPayer) {
      if (newPayer && newPayer !== oldPayer) {
        // Set flag to prevent service code watcher from clearing notes
        this.isChangingPayer = true;

        // Clear selected client and service codes when switching payer
        this.selectedClient = null;
        // Use null for single-select (both VR and Non Service now use single-select)
        this.selectedServiceCodes = null;

        // Load clients if switching to VR payer
        if (newPayer === "VR") {
          await this.loadClients();
        } else {
          // Load appropriate service codes for DSPD and Non Service payers
          await this.loadAvailableServiceCodes();
        }

        // Unset flag after service codes are loaded
        this.isChangingPayer = false;
      }
    },

    async selectedClient(newClient, oldClient) {
      // When client changes, reload service codes and clear selected service codes
      if (newClient !== oldClient && this.selectedPayer === "VR") {
        this.selectedServiceCodes = null;
        await this.loadAvailableServiceCodes();
      }
    },

    async selectedServiceCodes(newVal, oldVal) {
      // Only react if the service code actually changed (not just initial load)
      // Skip if we're loading existing data to prevent clearing the data we're trying to load
      // Skip if we're changing payers to prevent clearing notes when payer change triggers service code clear
      if (newVal !== oldVal && oldVal !== undefined && !this.isLoadingExistingData && !this.isChangingPayer) {
        // Determine the type of the old and new service codes
        const getServiceType = (serviceCodeId) => {
          if (!serviceCodeId) return null;
          const allCodes = [...this.serviceCodes, ...this.nonServiceCodes];
          const code = allCodes.find(sc => sc.id === serviceCodeId);
          if (!code) return null;

          // Check if it's a JD code
          if (["CE", "SE", "SJBT", "TWE"].includes(code.code)) return "JD";
          // Check if it's a JC code
          if (code.code === "JC") return "JC";
          // Check if it's LS, SED, SB, etc.
          if (["LS", "SED", "SB", "DSR", "WSA1", "WSA2", "SELF"].includes(code.code)) return "OTHER";
          return "OTHER";
        };

        const oldType = getServiceType(oldVal);
        const newType = getServiceType(newVal);

        // Only clear fields if switching between different service types
        const switchingTypes = oldType !== newType;

        if (switchingTypes) {
          // Clear all activity-related data when service code type changes
          this.selectedActivities = [];
          this.selectedSEDActivities = [];
          this.selectedSBActivities = [];
          this.selectedJCPrimaryActivities = [];
          this.selectedJCSecondaryActivities = [];

          // Clear all note fields when switching between different service types
          this.shiftNotes = "";
          this.jdActivity = "";
          this.jdOutcome = "";
          this.jdNextSteps = "";
        } else {
          // Switching within the same type - keep the notes but clear activities if needed
          if (oldType === "OTHER") {
            // Clear activity selections when switching between non-JD/non-JC service codes
            this.selectedActivities = [];
            this.selectedSEDActivities = [];
            this.selectedSBActivities = [];
          }
        }

        // Load job coaching activities if switching to JC service code
        if (this.isJCService && this.jobCoachingActivities.length === 0) {
          try {
            this.loadingJobCoachingActivities = true;
            const response = await jobCoachingActivitiesService.list();
            this.jobCoachingActivities = response || [];
          } catch (error) {
            this.$notifyError("Failed to load job coaching activities");
          } finally {
            this.loadingJobCoachingActivities = false;
          }
        }
      }
    },
  },

  methods: {
    async loadServiceCodes() {
      try {
        this.loadingServiceCodes = true;
        // Load VR service codes, DSPD service codes, and non-service codes
        const [serviceCodes, dspdServiceCodes, nonServiceCodes] = await Promise.all([
          serviceCodesService.listVR(),
          serviceCodesService.listDSPD(),
          nonServiceCodesService.list(),
        ]);
        this.serviceCodes = serviceCodes || [];
        this.dspdServiceCodes = dspdServiceCodes || [];
        this.nonServiceCodes = nonServiceCodes || [];
      } catch (error) {
        this.serviceCodes = [];
        this.dspdServiceCodes = [];
        this.nonServiceCodes = [];
      } finally {
        this.loadingServiceCodes = false;
      }
    },

    async loadClients() {
      try {
        this.loadingClients = true;
        const clients = await clientsService.list({ slim: true });
        this.clients = (clients || []).map(client => ({
          id: client.id,
          name: `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unnamed Client',
        }));
      } catch (error) {
        this.$notifyError("Failed to load clients");
        this.clients = [];
      } finally {
        this.loadingClients = false;
      }
    },

    async loadAvailableServiceCodes() {
      try {
        // Load appropriate service codes based on payer type
        let codes = [];
        if (this.selectedPayer === "Non Service") {
          // Use non-service codes
          codes = this.nonServiceCodes || [];
        } else if (this.selectedPayer === "VR") {
          // For VR payer, fetch service codes from client authorizations
          if (this.selectedClient) {
            const serviceCodes = await serviceCodesService.getByClient({
              clientId: this.selectedClient,
              payer_type: 'VR'
            });
            codes = serviceCodes || [];
          } else {
            // No client selected, don't load any service codes yet
            codes = [];
          }
        } else if (this.selectedPayer === "DSPD") {
          // For DSPD payer, fetch service codes by client auth
          const clientId = this.selectedClient || this.shift?.clientId || this.shift?.clients?.[0]?.id;
          if (clientId) {
            const dspdCodes = await serviceCodesService.getByClient({
              clientId,
              payer_type: "DSPD",
            });
            codes = dspdCodes || [];
            // For 1-on-1 shifts, exclude group codes (DSP, DSG)
            if (!this.isGroupShift) {
              codes = codes.filter(sc => sc.code !== "DSP" && sc.code !== "DSG");
            }
          } else {
            codes = [];
          }
        } else {
          // For other payers, use all service codes
          codes = this.serviceCodes || [];
        }

        // Map to display abbreviated codes instead of full names
        this.availableServiceCodes = codes.map((sc) => ({
          id: sc.id,
          name: sc.code || sc.name, // Use code (e.g., "LS", "JC") instead of full name
          fullName: sc.name, // Keep full name for reference if needed
        }));
      } catch (error) {
        this.availableServiceCodes = [];
      }
    },

    // vrActivities mixin hook: this modal is single-code (form-driven).
    getShiftVrCodes() {
      return this.serviceCode ? [this.serviceCode] : [];
    },

    async loadActivityOptions() {
      // Generic VR activity lists (LS/SED/SB and any other VR code) are loaded
      // by the vrActivities mixin, keyed by this shift's service code.
      await this.loadVrActivities();

      // Load JC activities
      if (this.isJCService) {
        try {
          this.loadingJobCoachingActivities = true;

          const response = await jobCoachingActivitiesService.list();
          this.jobCoachingActivities = response || [];
        } catch (error) {
          this.$notifyError("Failed to load job coaching activities");
        } finally {
          this.loadingJobCoachingActivities = false;
        }
      }
    },

    async loadExistingData() {
      if (!this.shift) {
        return;
      }

      // Set flag to prevent watchers from clearing data during load
      this.isLoadingExistingData = true;

      // Load payer type
      this.selectedPayer = this.shift.payer || null;

      // Always load clients list so dropdown is populated
      await this.loadClients();

      // Load client if VR payer
      if (this.selectedPayer === "VR") {
        const clientId = this.shift?.clientId || this.shift?.clients?.[0]?.id;
        if (clientId) {
          this.selectedClient = clientId;
        }
      }

      // Load service codes
      const serviceCodes =
        this.shift.service_codes || this.shift.serviceCodes || [];
      const nonServiceCodes =
        this.shift.non_service_codes || this.shift.nonServiceCodes || [];

      // Load available service codes based on payer
      await this.loadAvailableServiceCodes();

      // Set selected service codes
      if (this.selectedPayer === "Non Service" && nonServiceCodes.length > 0) {
        // For Non Service (single-select mode), set as single value not array
        this.selectedServiceCodes = nonServiceCodes[0].id;
      } else if (serviceCodes.length > 0) {
        // For VR payer (now also single-select mode), set as single value
        this.selectedServiceCodes = serviceCodes[0].id;
      }

      // Load existing notes based on service type
      if (this.isJDService) {
        this.jdActivity = this.shift.jd_activity || "";
        this.jdOutcome = this.shift.jd_outcome || "";
        this.jdNextSteps = this.shift.jd_next_steps || "";
      } else {
        // For all non-JD services (LS, SED, SB, JC, etc.), use shift_notes
        this.shiftNotes = this.shift.shift_notes || this.shift.notes || "";
      }
    },

    async loadExistingActivityData() {
      if (!this.shift) {
        return;
      }

      // Load existing activity selections - Activities are stored as names (strings),
      // but AIVSelect needs IDs, so we map names back to IDs
      // This runs after loadActivityOptions() so the activity arrays are populated

      // Generic VR activities (LS/SED/SB and any other VR code): map saved
      // names back to option ids via the mixin.
      this.loadSelectedVrActivities(this.shift);

      if (this.isJCService) {
        if (this.shift.jc_primary_activities) {
          // Parse if it's a JSON string, otherwise use as is
          let activities = this.shift.jc_primary_activities;
          if (typeof activities === "string") {
            try {
              activities = JSON.parse(activities);
            } catch (error) {
              activities = [];
            }
          }

          if (Array.isArray(activities)) {
            this.selectedJCPrimaryActivities = activities
              .map((activityName) => {
                const activity = this.jobCoachingActivities.find(
                  (act) => act.name === activityName,
                );
                return activity ? activity.id : null;
              })
              .filter(Boolean);
          }
        }

        if (this.shift.jc_secondary_activities) {
          // Parse if it's a JSON string, otherwise use as is
          let activities = this.shift.jc_secondary_activities;
          if (typeof activities === "string") {
            try {
              activities = JSON.parse(activities);
            } catch (error) {
              activities = [];
            }
          }

          if (Array.isArray(activities)) {
            this.selectedJCSecondaryActivities = activities
              .map((activityName) => {
                const activity = this.jobCoachingActivities.find(
                  (act) => act.name === activityName,
                );
                return activity ? activity.id : null;
              })
              .filter(Boolean);
          }
        }
      }

      // Unset flag after all data is loaded
      this.isLoadingExistingData = false;
    },

    async loadGroupShiftNotes() {
      this.loadingGroupNotes = true;
      try {
        // Fetch service code questions
        const serviceCode = this.shiftServiceCode;
        if (serviceCode) {
          try {
            this.serviceCodeQuestions = await shiftNoteQuestionsService.getByServiceCode(serviceCode);
          } catch (e) {
            this.serviceCodeQuestions = [];
          }
        }

        // Fetch client goals for each client in the shift
        const clientDetails = this.shift.clientDetails || this.shift.originalData?.clientDetails || [];
        const goalsPromises = clientDetails.map(async (cd) => {
          try {
            const response = await clientsService.getGoals(cd.id_clients);
            this.$set(this.clientGoals, cd.id_clients, response.goals || []);
          } catch (e) {
            this.$set(this.clientGoals, cd.id_clients, []);
          }
        });
        await Promise.all(goalsPromises);

        // Initialize notesData for each client
        clientDetails.forEach((cd) => {
          if (!this.notesData[cd.id_clients]) {
            const questionAnswers = {};
            this.serviceCodeQuestions.forEach((q) => {
              questionAnswers[q.id] = "";
            });

            const goalAnswers = {};
            const goals = this.clientGoals[cd.id_clients] || [];
            goals.forEach((g) => {
              goalAnswers[g.id] = "";
            });

            this.$set(this.notesData, cd.id_clients, {
              shiftNotes: "",
              questionAnswers,
              goalAnswers,
            });
          }
        });

        // Load existing shift notes text from client details
        clientDetails.forEach((cd) => {
          if (cd.shift_notes && this.notesData[cd.id_clients]) {
            this.notesData[cd.id_clients].shiftNotes = cd.shift_notes;
          }
        });

        // Load existing question answers from shift-level shiftNoteAnswers
        const allQuestionAnswers = this.shift?.shiftNoteAnswers || this.shift?.originalData?.shiftNoteAnswers || [];
        for (const answer of allQuestionAnswers) {
          const cId = answer.client_id;
          if (this.notesData[cId]) {
            this.$set(this.notesData[cId].questionAnswers, answer.question_id, answer.answer || "");
          }
        }

        // Build goal-to-client mapping so goal answers go under the correct client
        const goalToClient = {};
        for (const [cId, goalsList] of Object.entries(this.clientGoals)) {
          for (const goal of goalsList) {
            goalToClient[goal.id] = Number(cId);
          }
        }

        // Load existing goal answers from shift-level goalAnswers
        const allGoalAnswers = this.shift?.goalAnswers || this.shift?.originalData?.goalAnswers || [];
        for (const answer of allGoalAnswers) {
          const ownerClientId = goalToClient[answer.id_goals];
          if (ownerClientId && this.notesData[ownerClientId]) {
            this.$set(this.notesData[ownerClientId].goalAnswers, answer.id_goals, answer.answer || "");
          }
        }

        // Expand all clients by default so the user can see all answers
        clientDetails.forEach((cd) => {
          this.$set(this.expandedClients, cd.id_clients, true);
        });

        this.groupNotesFetched = true;
      } catch (error) {
        this.$notifyError("Error loading shift notes data: " + (error.response?.data?.error || error.message));
      } finally {
        this.loadingGroupNotes = false;
      }
    },

    async loadDSPDOneOnOneNotes() {
      // Fetch service code questions
      const serviceCodes = this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
      if (serviceCodes.length > 0) {
        try {
          const questionPromises = serviceCodes.map((sc) =>
            shiftNoteQuestionsService.getByServiceCode(sc.code).catch(() => []),
          );
          const questionResults = await Promise.all(questionPromises);
          const allQuestions = [];
          const seenIds = new Set();
          for (const questions of questionResults) {
            for (const q of (questions || [])) {
              if (!seenIds.has(q.id)) {
                seenIds.add(q.id);
                allQuestions.push(q);
              }
            }
          }
          this.dspdServiceCodeQuestions = allQuestions.sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
          );
        } catch {
          this.dspdServiceCodeQuestions = [];
        }
      }

      // Fetch client goals
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      if (clients.length > 0) {
        const clientId = clients[0].id;
        if (clientId) {
          try {
            const response = await clientsService.getGoals(clientId);
            this.dspdClientGoals = response.goals || [];
          } catch {
            this.dspdClientGoals = [];
          }
        }
      }

      // Load existing question answers
      // Normalize yes/no answers to lowercase for consistent comparison
      const allAnswers = this.shift?.shiftNoteAnswers || this.shift?.originalData?.shiftNoteAnswers || [];
      for (const answer of allAnswers) {
        let ans = answer.answer || "";
        if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
          ans = ans.toLowerCase();
        }
        this.$set(this.dspdQuestionAnswers, answer.question_id, ans);
      }

      // Load existing goal answers
      // Normalize yes/no answers to lowercase for consistent comparison
      const allGoalAnswers = this.shift?.goalAnswers || this.shift?.originalData?.goalAnswers || [];
      for (const answer of allGoalAnswers) {
        let ans = answer.answer || "";
        if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
          ans = ans.toLowerCase();
        }
        this.$set(this.dspdGoalAnswers, answer.id_goals, ans);
      }

      // Load existing concerns (stored as shift_notes)
      this.dspdConcerns = this.shift?.shift_notes || this.shift?.originalData?.shift_notes || "";

      this.dspdOneOnOneLoaded = true;
    },

    async loadVRNotes() {
      // Fetch admin-defined service-code questions for this VR shift's service
      // codes (managed in Settings -> Edit VR Shift Questions), mirroring
      // ClockOutModal so they can be backfilled after the fact.
      this.vrQuestionAnswers = {};
      const serviceCodes =
        this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
      if (serviceCodes.length === 0) {
        this.vrServiceCodeQuestions = [];
        this.vrNotesLoaded = true;
        return;
      }
      try {
        const results = await Promise.all(
          serviceCodes.map((sc) =>
            shiftNoteQuestionsService.getByServiceCode(sc.code).catch(() => []),
          ),
        );
        const all = [];
        const seen = new Set();
        for (const qs of results) {
          for (const q of qs || []) {
            if (!seen.has(q.id)) {
              seen.add(q.id);
              all.push(q);
            }
          }
        }
        this.vrServiceCodeQuestions = all.sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
        );
      } catch {
        this.vrServiceCodeQuestions = [];
      }

      // Load existing answers (normalize yes/no to lowercase for comparison).
      // Shifts-list rows carry an empty shiftNoteAnswers placeholder, so fall
      // back to the tasks endpoint when the prop has none.
      let allAnswers =
        this.shift?.shiftNoteAnswers || this.shift?.originalData?.shiftNoteAnswers || [];
      if (allAnswers.length === 0) {
        try {
          const fullShift = await shiftsService.getShiftTasks(
            this.shift.originalData?.id || this.shift.id,
          );
          allAnswers = fullShift?.shiftNoteAnswers || [];
        } catch {
          allAnswers = [];
        }
      }
      for (const answer of allAnswers) {
        let ans = answer.answer || "";
        if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
          ans = ans.toLowerCase();
        }
        this.$set(this.vrQuestionAnswers, answer.question_id, ans);
      }

      this.vrNotesLoaded = true;
    },

    async loadMedicationData() {
      if (!this.isDSPDOneOnOneShift || this.isClockedInEditMode) return;
      this.loadingMedications = true;
      try {
        const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
        const clientId = clients.length > 0 ? clients[0].id : null;
        if (!clientId) return;

        // Fetch existing medication logs for this shift
        let existingLogs = [];
        try {
          const response = await shiftsService.getShiftTasks(this.shift.id);
          const shiftData = response.data || response;
          if (shiftData.medicationAdministrationLogs) {
            existingLogs = shiftData.medicationAdministrationLogs.filter(
              log => Number(log.id_clients) === Number(clientId)
            );
          }
        } catch { /* no existing logs */ }

        // Fetch client's active medications
        let meds = [];
        try {
          const response = await clientsService.getMedications(clientId);
          meds = (response.medications || []).filter(m => m.active !== false);
        } catch { /* no medications */ }

        this.clientMedications = meds;

        // Initialize editable state from existing logs
        const now = new Date();
        const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const administrations = {};
        for (const med of meds) {
          const key = `${clientId}_${med.id}`;
          const existingLog = existingLogs.find(
            log => Number(log.id_medications) === Number(med.id)
          );
          if (existingLog) {
            let timeStr = currentTimeStr;
            if (existingLog.administered_at) {
              const d = new Date(existingLog.administered_at);
              if (!isNaN(d.getTime())) {
                timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
              }
            }
            administrations[key] = {
              wasAdministered: existingLog.was_administered,
              skipReason: existingLog.skip_reason || "",
              clientId,
              medicationId: med.id,
              administeredAtTime: existingLog.was_administered ? timeStr : currentTimeStr,
            };
          } else {
            administrations[key] = {
              wasAdministered: false,
              skipReason: "",
              clientId,
              medicationId: med.id,
              administeredAtTime: currentTimeStr,
            };
          }
        }
        this.medicationAdministrations = administrations;
      } finally {
        this.loadingMedications = false;
      }
    },

    buildAdministeredAtISO(timeStr) {
      if (!timeStr) return new Date().toISOString();
      const [hours, minutes] = timeStr.split(':').map(Number);
      const dt = new Date();
      if (this.shift?.date) {
        const shiftDateStr = this.shift.date.split('T')[0];
        const [y, mo, d] = shiftDateStr.split('-').map(Number);
        dt.setFullYear(y, mo - 1, d);
      }
      dt.setHours(hours, minutes, 0, 0);
      return dt.toISOString();
    },

    getAnswer(clientId, answerType, questionId) {
      if (!this.notesData[clientId]) return "";
      return this.notesData[clientId][answerType]?.[questionId] || "";
    },

    setAnswer(clientId, answerType, questionId, value) {
      if (!this.notesData[clientId]) return;
      this.$set(this.notesData[clientId][answerType], questionId, value);
    },

    // multi_select answers are persisted as a human-readable, comma-joined
    // string (e.g. "Option A, Option B") so every read/display/report/AI path
    // renders them as-is. parse/join convert to/from the array the
    // multi-select control expects.
    parseMultiAnswer(value) {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined || value === "") return [];
      return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },

    joinMultiAnswer(value) {
      return (Array.isArray(value) ? value : []).join(", ");
    },

    setShiftNotes(clientId, value) {
      if (!this.notesData[clientId]) return;
      this.$set(this.notesData[clientId], 'shiftNotes', value);
    },

    toggleClientNotes(clientId) {
      this.$set(this.expandedClients, clientId, !this.expandedClients[clientId]);
    },

    formatClockTime(utcDateTimeStr) {
      if (!utcDateTimeStr) return "";
      const d = new Date(utcDateTimeStr);
      if (isNaN(d.getTime())) return "";
      let hours = d.getHours();
      const minutes = d.getMinutes();
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      return `${hours}:${String(minutes).padStart(2, "0")}${ampm}`;
    },

    getTaskCaregiverName(caregiverId) {
      if (!caregiverId) return "Unassigned";
      const caregivers = this.shift?.caregivers || [];
      const cg = caregivers.find(c => Number(c.id) === Number(caregiverId));
      if (!cg) return "Unknown";
      return cg.name || `${cg.first_name || ""} ${cg.last_name || ""}`.trim() || "Unknown";
    },

    async handleSave() {
      // The API rejects Admin shift edits without a reason; every non-group
      // save below goes through PUT /api/shifts/:id.
      if (this.isAdminUser && !this.isGroupShift && !this.adminChangeReason.trim()) {
        this.$notifyError("Please provide a reason for this edit");
        return;
      }

      try {
        this.saving = true;

        // For clocked-in 1-on-1 DSPD shifts, only save payer and service code
        if (this.isClockedInEditMode) {
          const updateData = {};
          if (this.selectedPayer) updateData.payer = this.selectedPayer;
          if (this.selectedServiceCodes != null && this.selectedServiceCodes !== '') {
            updateData.serviceCodeIds = [this.selectedServiceCodes];
          }
          if (this.adminChangeReason.trim()) {
            updateData.adminChangeReason = this.adminChangeReason.trim();
          }

          await shiftsService.update(this.shift.id, updateData);
          this.$notifySuccess("Shift details updated successfully");
          this.$emit("updated");
          this.isVisible = false;
          return;
        }

        // For group shifts, save per-client notes via completeShiftNotes
        if (this.isGroupShift && this.groupNotesFetched) {
          // Validate each client's notes (only ones assigned to current user)
          this.validationErrors = [];
          for (const task of this.myClientNotes) {
            const data = this.notesData[task.clientId];
            if (!data) continue;

            // Validate all service code questions
            for (const q of this.serviceCodeQuestions) {
              const answer = data.questionAnswers[q.id];
              if (q.question_type === "yes_no") {
                if (answer !== "yes" && answer !== "no") {
                  this.validationErrors.push(
                    `Please select Yes or No for "${q.question_text}" for ${task.clientName}`,
                  );
                }
              } else if (q.question_type === "free_response") {
                if (!answer || answer.trim().length < 20) {
                  this.validationErrors.push(
                    `Response must be at least 20 characters for "${q.question_text}" for ${task.clientName}`,
                  );
                }
              }
            }

            // Validate all client goal questions
            const goals = this.clientGoals[task.clientId] || [];
            for (const goal of goals) {
              const answer = data.goalAnswers[goal.id];
              if (goal.question_type === "yes_no") {
                if (answer !== "yes" && answer !== "no") {
                  this.validationErrors.push(
                    `Please select Yes or No for "${goal.question_text}" for ${task.clientName}`,
                  );
                }
              } else if (goal.question_type === "free_response") {
                if (!answer || answer.trim().length < 20) {
                  this.validationErrors.push(
                    `Response must be at least 20 characters for "${goal.question_text}" for ${task.clientName}`,
                  );
                }
              }
            }

            // Validate general shift notes (concerns)
            if (!data.shiftNotes || data.shiftNotes.trim().length < 20) {
              this.validationErrors.push(
                `Response must be at least 20 characters for "Do you have any concerns about this individual today?" for ${task.clientName}`,
              );
            }
          }

          if (this.validationErrors.length > 0) {
            this.$notifyError(`Please fix ${this.validationErrors.length} error(s) before submitting.`);
            this.saving = false;
            this.$nextTick(() => {
              const errorSummary = this.$el.querySelector('.validation-error-summary');
              if (errorSummary) errorSummary.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
            return;
          }

          // Save notes for each client assigned to current user
          for (const task of this.myClientNotes) {
            const data = this.notesData[task.clientId];
            if (!data) continue;

            const questionAnswers = Object.entries(data.questionAnswers)
              .filter(([, answer]) => answer && answer.toString().trim())
              .map(([questionId, answer]) => ({
                questionId: Number(questionId),
                answer: answer.toString().trim(),
              }));

            const goalAnswers = Object.entries(data.goalAnswers)
              .filter(([, answer]) => answer && answer.toString().trim())
              .map(([goalId, answer]) => ({
                goalId: Number(goalId),
                answer: answer.toString().trim(),
              }));

            await shiftsService.completeShiftNotes({
              shiftId: this.shift.id,
              clientId: task.clientId,
              caregiverId: Number(this.userId),
              shiftNotes: data.shiftNotes.trim(),
              questionAnswers,
              goalAnswers,
            });
          }

          this.$notifySuccess("Shift notes updated successfully");
          this.$emit("updated");
          this.isVisible = false;
          return;
        }

        // For DSPD 1-on-1 shifts, save via completeShiftNotes (reuse same endpoint)
        if (this.isDSPDOneOnOneShift && this.dspdOneOnOneLoaded) {
          // Validate required service code questions (rendered list only)
          for (const q of this.visibleDspdQuestions) {
            if (q.required) {
              const answer = this.dspdQuestionAnswers[q.id];
              if (!answer || !answer.toString().trim()) {
                this.$notifyError(`Please answer: "${q.question_text}"`);
                this.saving = false;
                return;
              }
            }
          }

          // Validate the concerns textarea (only when it is shown)
          if (this.showConcernsField && !this.dspdConcerns.trim()) {
            this.$notifyError("Please answer the concerns question");
            this.saving = false;
            return;
          }

          // Validate medication skip reasons
          if (this.clientMedications.length > 0) {
            for (const key of Object.keys(this.medicationAdministrations)) {
              const ma = this.medicationAdministrations[key];
              if (!ma.wasAdministered && !ma.skipReason?.trim()) {
                this.$notifyError("Please provide a reason for each skipped medication");
                this.saving = false;
                return;
              }
            }
          }

          const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
          const clientId = clients.length > 0 ? clients[0].id : null;

          if (clientId) {
            const questionAnswers = Object.entries(this.dspdQuestionAnswers)
              .filter(([, answer]) => answer != null && answer.toString().trim())
              .map(([questionId, answer]) => ({
                questionId: Number(questionId),
                answer: answer.toString().trim(),
              }));

            const goalAnswers = Object.entries(this.dspdGoalAnswers)
              .filter(([, answer]) => answer != null && answer.toString().trim())
              .map(([goalId, answer]) => ({
                goalId: Number(goalId),
                answer: answer.toString().trim(),
              }));

            // Update shift notes (concerns) and Q&A data
            const updateData = {};
            if (this.selectedPayer) updateData.payer = this.selectedPayer;
            if (this.selectedServiceCodes != null && this.selectedServiceCodes !== '') {
              updateData.serviceCodeIds = [this.selectedServiceCodes];
            }
            if (this.showConcernsField) {
              updateData.shift_notes = this.dspdConcerns.trim();
            }
            updateData.questionAnswers = questionAnswers;
            updateData.goalAnswers = goalAnswers;
            if (this.adminChangeReason.trim()) {
              updateData.adminChangeReason = this.adminChangeReason.trim();
            }

            // Add medication administrations if any medications exist
            if (this.clientMedications.length > 0) {
              updateData.medicationAdministrations = Object.values(this.medicationAdministrations).map(ma => ({
                clientId: ma.clientId,
                medicationId: ma.medicationId,
                wasAdministered: ma.wasAdministered,
                skipReason: ma.wasAdministered ? null : (ma.skipReason || null),
                administeredAt: ma.wasAdministered ? this.buildAdministeredAtISO(ma.administeredAtTime) : null,
              }));
            }

            await shiftsService.update(this.shift.id, updateData);
          }

          this.$notifySuccess("Shift notes updated successfully");
          this.$emit("updated");
          this.isVisible = false;
          return;
        }

        // Non-group shift save logic
        // Validate shift notes - must be at least 20 characters
        if (this.showJdFields) {
          // For JD services, validate the three required fields
          if (this.jdActivity.length < 20) {
            this.$notifyError(
              "Job Development Activity must be at least 20 characters",
            );
            this.saving = false;
            return;
          }
          if (this.jdOutcome.length < 20) {
            this.$notifyError(
              "Outcome of Activity must be at least 20 characters",
            );
            this.saving = false;
            return;
          }
          if (this.jdNextSteps.length < 20) {
            this.$notifyError(
              "Next Steps Forward must be at least 20 characters",
            );
            this.saving = false;
            return;
          }
        } else if (!this.hasShiftNoteQuestions) {
          // For all other services, validate shift notes — skipped when
          // shift-note questions apply (freeform box hidden; questions carry
          // their own required-field validation below).
          if (this.shiftNotes.length < 20) {
            this.$notifyError("Shift Notes must be at least 20 characters");
            this.saving = false;
            return;
          }
        }

        // Validate required VR service-code questions
        if (this.isVRShift && this.vrServiceCodeQuestions.length && !this.showJdFields) {
          for (const q of this.vrServiceCodeQuestions) {
            if (q.required) {
              const ans = this.vrQuestionAnswers[q.id];
              if (!ans || !ans.toString().trim()) {
                this.$notifyError(`Please answer: "${q.question_text}"`);
                this.saving = false;
                return;
              }
            }
          }
        }

        const updateData = {};

        // Add payer type
        if (this.selectedPayer) {
          updateData.payer = this.selectedPayer;
        }

        // Add client ID for VR payer, or clear it for Non Service
        if (this.selectedPayer === "VR") {
          if (this.selectedClient) {
            updateData.clientId = this.selectedClient;
          }
        } else if (this.selectedPayer !== "DSPD") {
          // Clear client when switching to Non Service (but not DSPD - clients are part of group shift)
          updateData.clientIds = [];
        }

        // Add service codes - both VR and Non Service now use single-select
        if (this.selectedServiceCodes != null && this.selectedServiceCodes !== '') {
          // Normalize to array format for API
          updateData.serviceCodeIds = [this.selectedServiceCodes];
        }

        // Generic VR activities (LS/SED/SB and any other VR code). This is a
        // single-code modal, so MERGE into any existing selections to avoid
        // wiping other codes' activities on a multi-code shift.
        updateData.vr_activities_selected = this.mergeVrActivitiesPayload(
          this.shift?.vr_activities_selected ??
            this.shift?.originalData?.vr_activities_selected,
        );
        if (this.showJdFields) {
          if (this.jdActivity.trim()) {
            updateData.jd_activity = this.jdActivity;
          }
          if (this.jdOutcome.trim()) {
            updateData.jd_outcome = this.jdOutcome;
          }
          if (this.jdNextSteps.trim()) {
            updateData.jd_next_steps = this.jdNextSteps;
          }
        }
        if (this.isJCService) {
          if (this.selectedJCPrimaryActivities.length > 0) {
            updateData.jc_primary_activities = this.selectedJCPrimaryActivities
              .map(
                (actId) =>
                  this.jobCoachingActivities.find((act) => act.id === actId)
                    ?.name,
              )
              .filter(Boolean);
          }
          if (this.selectedJCSecondaryActivities.length > 0) {
            updateData.jc_secondary_activities =
              this.selectedJCSecondaryActivities
                .map(
                  (actId) =>
                    this.jobCoachingActivities.find((act) => act.id === actId)
                      ?.name,
                )
                .filter(Boolean);
          }
        }

        // Add general shift notes for non-JD services
        if (!this.isJDService && this.shiftNotes.trim()) {
          updateData.shift_notes = this.shiftNotes;
        }

        // Add VR service-code question answers. Only when questions are configured,
        // so VR shifts without questions keep their existing save/history behavior.
        if (this.isVRShift && this.vrServiceCodeQuestions.length) {
          updateData.questionAnswers = Object.entries(this.vrQuestionAnswers)
            .filter(([, answer]) => answer != null && answer.toString().trim())
            .map(([questionId, answer]) => ({
              questionId: Number(questionId),
              answer: answer.toString().trim(),
            }));
        }

        if (this.adminChangeReason.trim()) {
          updateData.adminChangeReason = this.adminChangeReason.trim();
        }

        await shiftsService.update(this.shift.id, updateData);

        this.$notifySuccess("Shift updated successfully");
        this.$emit("updated");
        this.isVisible = false;
      } catch (error) {
        const isNetworkError = !navigator.onLine ||
          error.message === "Network Error" ||
          error.code === "ECONNABORTED";

        if (isNetworkError) {
          // Queue the submission for when connectivity returns
          offlineEnqueue("complete-shift-notes", {
            shiftId: this.shift.id,
            isGroupShift: this.isGroupShift && this.groupNotesFetched,
            notesData: this.isGroupShift ? this._buildGroupNotesPayload() : null,
            updateData: !this.isGroupShift ? this._buildUpdatePayload() : null,
          });
          const pendingCount = getOfflinePending().length;
          this.$notifySuccess(
            `Notes saved locally. They will be submitted automatically when you're back online. (${pendingCount} pending)`,
          );
          this.$emit("updated");
          this.isVisible = false;
        } else {
          this.$notifyError(
            error.response?.data?.error ||
              error.response?.data?.message ||
              "Failed to update shift. Please try again.",
          );
        }
      } finally {
        this.saving = false;
      }
    },

    _buildGroupNotesPayload() {
      const items = [];
      for (const task of this.myClientNotes) {
        const data = this.notesData[task.clientId];
        if (!data) continue;

        const questionAnswers = Object.entries(data.questionAnswers)
          .filter(([, answer]) => answer && answer.toString().trim())
          .map(([questionId, answer]) => ({
            questionId: Number(questionId),
            answer: answer.toString().trim(),
          }));

        const goalAnswers = Object.entries(data.goalAnswers)
          .filter(([, answer]) => answer && answer.toString().trim())
          .map(([goalId, answer]) => ({
            goalId: Number(goalId),
            answer: answer.toString().trim(),
          }));

        items.push({
          shiftId: this.shift.id,
          clientId: task.clientId,
          caregiverId: Number(this.userId),
          shiftNotes: data.shiftNotes.trim(),
          questionAnswers,
          goalAnswers,
        });
      }
      return items;
    },

    _buildUpdatePayload() {
      const updateData = {};
      if (this.selectedPayer) updateData.payer = this.selectedPayer;
      if (this.selectedServiceCodes != null && this.selectedServiceCodes !== "") {
        updateData.serviceCodeIds = [this.selectedServiceCodes];
      }
      if (!this.isJDService && this.shiftNotes.trim()) {
        updateData.shift_notes = this.shiftNotes;
      }
      if (this.isVRShift && this.vrServiceCodeQuestions.length) {
        updateData.questionAnswers = Object.entries(this.vrQuestionAnswers)
          .filter(([, answer]) => answer != null && answer.toString().trim())
          .map(([questionId, answer]) => ({
            questionId: Number(questionId),
            answer: answer.toString().trim(),
          }));
      }
      if (this.adminChangeReason.trim()) {
        updateData.adminChangeReason = this.adminChangeReason.trim();
      }
      return { shiftId: this.shift.id, data: updateData };
    },

    handleCancel() {
      this.validationErrors = [];
      this.isVisible = false;
    },

    resetForm() {
      this.isLoadingExistingData = false;
      this.isChangingPayer = false;
      this.selectedPayer = null;
      this.selectedClient = null;
      this.selectedServiceCodes = null;
      this.clients = [];
      this.availableServiceCodes = [];
      this.selectedActivities = [];
      this.selectedSEDActivities = [];
      this.selectedSBActivities = [];
      this.selectedJCPrimaryActivities = [];
      this.selectedJCSecondaryActivities = [];
      this.shiftNotes = "";
      this.jdActivity = "";
      this.jdOutcome = "";
      this.jdNextSteps = "";
      // Group shift data
      this.serviceCodeQuestions = [];
      this.clientGoals = {};
      this.notesData = {};
      this.expandedClients = {};
      this.loadingGroupNotes = false;
      this.groupNotesFetched = false;
      // VR question data
      this.vrServiceCodeQuestions = [];
      this.vrQuestionAnswers = {};
      this.vrNotesLoaded = false;
      // Medication data
      this.clientMedications = [];
      this.medicationAdministrations = {};
      this.loadingMedications = false;
      this.validationErrors = [];
      this.adminChangeReason = "";
    },

    formatTableDate(dateValue) {
      if (!dateValue) return "N/A";
      try {
        // Parse date string as local date to avoid timezone shift
        let dateStr = dateValue;
        if (dateValue instanceof Date) {
          dateStr = dateValue.toISOString().split("T")[0];
        }
        const [year, month, day] = dateStr.split("-");
        return `${month}/${day}/${year}`;
      } catch {
        return "N/A";
      }
    },

    formatTimeRange(start, end, date) {
      if (!start || !end) return "N/A";
      try {
        const formatTime = (time, shiftDate) => {
          let dateTime;
          if (time instanceof Date) {
            dateTime = time;
          } else if (typeof time === "string" && time.includes("T")) {
            dateTime = new Date(time);
          } else {
            const baseDate = shiftDate ? new Date(shiftDate) : new Date();
            const [hours, minutes] = time.split(":");
            dateTime = new Date(baseDate);
            dateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
          }
          return dateTime.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        };

        const startTime = formatTime(start, date);
        const endTime = formatTime(end, date);
        return `${startTime} - ${endTime}`;
      } catch {
        return "N/A";
      }
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients.map(client => {
          const name =
            client.name ||
            `${client.first_name || ""} ${client.last_name || ""}`.trim();
          return name || "Unnamed Client";
        }).join(", ");
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        return shift.caregivers.map(caregiver => {
          const name =
            caregiver.name ||
            `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim();
          return name || "Unnamed Caregiver";
        }).join(", ");
      }
      return shift.caregiver || "Unassigned";
    },

    formatServiceCode(shift) {
      if (!shift) return "Not specified";

      // Check for non-service codes first (for Non Service shifts)
      const nonServiceCodes =
        shift.non_service_codes ||
        shift.nonServiceCodes ||
        shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        const codes = nonServiceCodes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      // Check direct service_codes array (from by-role API)
      if (shift.service_codes && shift.service_codes.length > 0) {
        const codes = shift.service_codes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      return "Not specified";
    },
  },
};
</script>

<style scoped>

.validation-error-summary {
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.validation-error-header {
  color: #f56c6c;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
}

.validation-error-list {
  margin: 0;
  padding-left: 20px;
  color: #f56c6c;
  font-size: 13px;
}

.validation-error-list li {
  margin-bottom: 4px;
}

.edit-notes-content {
  max-width: 600px;
  min-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  overflow-x: hidden;
  box-sizing: border-box;
}

.edit-notes-content .mb-3 {
  margin-bottom: 1.5rem;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.edit-notes-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.edit-notes-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.edit-notes-content strong {
  color: #fff;
  font-weight: 600;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

:deep(.el-textarea__inner) {
  background-color: #656565 !important;
  border: 1px solid #bdbdbd !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-textarea__inner):focus {
  border-color: #ffffff !important;
  box-shadow: none !important;
}

:deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
}

@media (max-width: 768px) {
  /* Make modal wider on mobile devices */
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .edit-notes-content {
    min-width: auto !important;
    padding: 0 1rem;
  }
}

.shift-notes-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
}

.shift-notes-description ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  max-width: 100%;
  box-sizing: border-box;
}

.shift-notes-description li {
  margin-bottom: 0.25rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
}

.service-code-tooltip {
  color: #ffa726;
  font-size: 0.9rem;
  font-style: italic;
  padding: 0.5rem;
  background-color: rgba(255, 167, 38, 0.1);
  border-left: 3px solid #ffa726;
  border-radius: 4px;
}

/* Client Tasks (read-only) */
.client-tasks-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.client-task-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
}

.client-task-header {
  font-weight: 600;
  color: #fff;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.client-task-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.client-task-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.client-task-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e0e0e0;
}

.client-task-info i {
  width: 20px;
  text-align: center;
  color: #6ba6cd;
}

.client-task-label {
  font-size: 0.9rem;
}

.client-task-value {
  font-size: 0.9rem;
}

.client-task-completed {
  color: #4caf50;
  font-weight: 500;
}

.client-task-by {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
}

.client-task-pending {
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

/* Client Notes (expandable) */
.client-notes-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.client-notes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  background: rgba(255, 255, 255, 0.03);
}

.client-notes-header:hover {
  background: rgba(255, 255, 255, 0.08);
}

.client-notes-body {
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.required-indicator {
  color: #f44336;
  margin-left: 2px;
}

.yes-no-buttons {
  display: flex;
  gap: 0.5rem;
}

.mb-4 {
  margin-bottom: 2rem;
}
</style>
